import React from "react";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { getProfileImageUrl } from "helpers/Image.helper";
import { useNavigate } from "react-router-dom";
import { getStudentNickname, getFlagByCountryCode, getCurrentLangName } from "helpers/String.helper";
import StudentPhotos from "./StudentPhotos";
import StudentDiaryList from "./StudentDiaryList";

const StudentInfoComponent = (props: any) => {
    const navigate = useNavigate();

    const { isInit,
        info, errorMessage, handleFollow, handleUnfollow, isOnNetworking, studentId } = props;

    return (
        !isInit ?
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                    {/* 헤더 스켈레톤 */}
                    <div className="fixed top-0 left-0 w-full bg-gray-50/80 backdrop-blur-md z-10">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
                            <div className="flex items-center gap-4">
                                <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                                    <div className="flex flex-col gap-1">
                                        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                                        <div className="w-12 h-3 bg-gray-200 rounded animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="w-16 h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                    </div>

                    {/* 통계 영역 스켈레톤 */}
                    <div className="bg-white p-3 rounded-lg shadow-sm mb-4">
                        <div className="space-y-1">
                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-b-0">
                                    <div className="w-24 h-3 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="w-8 h-4 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 사진 갤러리 스켈레톤 */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="w-32 h-4 bg-gray-200 rounded animate-pulse mb-4"></div>
                        <div className="grid grid-cols-2 gap-2">
                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} className="aspect-square bg-gray-200 rounded-lg animate-pulse"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
            :
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                    <div className="fixed top-0 left-0 w-full bg-gray-50/80 backdrop-blur-md z-10">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
                            <div className="flex items-center gap-4">
                                <button onClick={() => navigate('/student')} className="text-gray-600 hover:text-gray-900">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <div className="flex items-center gap-3">
                                    {info && (
                                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden border-2 border-[#CF3688]/20">
                                            <img 
                                                src={getProfileImageUrl(info?.profileImage)} 
                                                alt={getStudentNickname(info, null)} 
                                                className="w-full h-full object-cover" 
                                            />
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <h1 className="text-sm font-bold text-gray-900">
                                                {info ? getStudentNickname(info, null) : getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtTitle")}
                                            </h1>
                                            {info && info?.countryInfo?.code && (
                                                <span className="text-sm">
                                                    {getFlagByCountryCode(info?.countryInfo?.code)}
                                                </span>
                                            )}
                                        </div>
                                        {info && info?.birthyear && (
                                            <div className="flex items-center">
                                                <span className="text-xs text-gray-400">
                                                    {info.birthyear}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {info && !info?.isMine && (
                                <div className="flex items-center">
                                    {info?.isFollowing ?
                                        <button
                                            disabled={isOnNetworking}
                                            onClick={() => handleUnfollow(info?.id)}
                                            className="px-3 py-1.5 bg-[#CF3688] text-white rounded text-xs font-medium hover:bg-[#CF3688]/90 transition-colors">
                                            {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtUnfollow")}
                                        </button>
                                        :
                                        <button
                                            disabled={isOnNetworking}
                                            onClick={() => handleFollow(info?.id)}
                                            className="px-3 py-1.5 bg-[#CF3688] text-white rounded text-xs font-medium hover:bg-[#CF3688]/90 transition-colors">
                                            {!info.isFollowing && info.isFollower
                                                ? getLanguageValue(PAGE_TYPE.COMMON, "btn.txtFollowBack")
                                                : info.isFollowing
                                                    ? getLanguageValue(PAGE_TYPE.COMMON, "btn.txtUnfollow")
                                                    : getLanguageValue(PAGE_TYPE.COMMON, "btn.txtFollow")}
                                        </button>
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                    {errorMessage &&
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-red-600 font-medium">{errorMessage}</p>
                            </div>
                        </div>
                    }
                    {info &&
                        <div className="block relative">
                            {!info?.isActiveOfPublicProfile &&
                                <div className="mb-8">
                                    <div className="block relative mb-6">
                                        <p className="text-[#cf3688] font-medium text-center">
                                            {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtDisabledPublicProfile")}
                                        </p>
                                    </div>
                                </div>
                            }

                            {info?.isActiveOfPublicProfile &&
                                <>
                                    {/* 통계 영역 */}
                                    <div className="bg-white p-3 rounded-lg shadow-sm mb-4">
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center py-1 border-b border-gray-100">
                                                <p className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtProfileManagerViewCount")}</p>
                                                <p className="text-sm font-bold text-gray-800">
                                                    {info?.castingManagerViewCount || 0}
                                                </p>
                                            </div>
                                            <div className="flex justify-between items-center py-1 border-b border-gray-100">
                                                <p className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtFavoriteByAgency")}</p>
                                                <p className="text-sm font-bold text-gray-800">
                                                    {info?.favoriteCount || 0}
                                                </p>
                                            </div>
                                            <div className="flex justify-between items-center py-1 border-b border-gray-100">
                                                <p className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtFollower")}</p>
                                                <p className="text-sm font-bold text-gray-800">
                                                    {info?.followerCount || 0}
                                                </p>
                                            </div>
                                            <div className="flex justify-between items-center py-1">
                                                <p className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtFollowing")}</p>
                                                <p className="text-sm font-bold text-gray-800">
                                                    {info?.followingCount || 0}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <StudentPhotos 
                                        studentId={props.studentId}
                                        isActiveOfPublicProfile={info?.isActiveOfPublicProfile}
                                        userStudentInfo={info}
                                    />

                                    <StudentDiaryList 
                                        studentId={props.studentId}
                                        isActiveOfPublicProfile={info?.isActiveOfPublicProfile}
                                    />
                                </>
                            }
                        </div>
                    }
                </div>
            </main>
    )
};

export default StudentInfoComponent;